import type { Form } from '../../payload-types';

/**
 * Which Form a Calendly Booking belongs to. Calendly's `event_scheduled`
 * postMessage reaches every listener on the page and names no Form, so each
 * `CalendlyTracker` asks `calendlyBookingFormId(event.source)` and acts only
 * when the answer is its own Form: each Booking credits exactly one Form,
 * however many booking Forms the page holds.
 *
 * Module state, in the visitor's browser: one page, one answer.
 */

type InlineFrame = {
  formId: Form['id'];
  getFrameWindow: () => Window | null | undefined;
};

const inlineFrames = new Set<InlineFrame>();
let lastPopupFormId: Form['id'] | null = null;

/** Called by `openCalendlyPopup` as it opens a Form's popup. */
export function noteCalendlyPopupOpened(formId: Form['id']): void {
  lastPopupFormId = formId;
}

/**
 * Registers a Form's inline Calendly widget. `getFrameWindow` is read when a
 * message arrives, so it may return nothing until the iframe has mounted.
 * Returns the cleanup that forgets it.
 */
export function registerInlineCalendlyFrame(
  formId: Form['id'],
  getFrameWindow: () => Window | null | undefined,
): () => void {
  const frame: InlineFrame = { formId, getFrameWindow };
  inlineFrames.add(frame);
  return () => {
    inlineFrames.delete(frame);
  };
}

/**
 * The Form a Calendly message from `source` belongs to: the Form whose inline
 * iframe's window sent it, else the Form whose popup opened last, else `null`
 * (a popup opened some other way, such as a custom script, credits no Form).
 */
export function calendlyBookingFormId(source: MessageEventSource | null): Form['id'] | null {
  if (source) {
    for (const frame of inlineFrames) {
      if (frame.getFrameWindow() === source) return frame.formId;
    }
  }
  return lastPopupFormId;
}

/** Test-only reset of the module state. */
export function _resetCalendlyBookingOwnerForTests(): void {
  inlineFrames.clear();
  lastPopupFormId = null;
}
